/**
 * AI Chat Service
 * Handles communication with Azure OpenAI for AI chatbot capabilities
 * Based on ai-chatbot-template-creation-6d168a.md and ai-data-aware-implementation-plan-85f4b6.md
 */

import type {
  AIResponse,
  AIGenerateReportRequest,
  AIRefineConfigRequest,
  AIAnalyzePDFRequest,
  AISchemaInfo,
  AIDataSummary,
  AIApiClient,
  AgentSurface,
} from '../types';

/**
 * Map the report-builder `reportType` discriminator to the AI chat surface the
 * request originates from. This is app context (where the user is), NOT a backend
 * agent/skill decision — the backend derives the route from the surface.
 */
function surfaceForReportType(reportType?: string): AgentSurface {
  switch (reportType) {
    case 'dashboard':
      return 'dashboard';
    case 'report':
      return 'report';
    case 'document':
      return 'template-interview';
    default:
      return 'general';
  }
}

class AIChatService {
  private apiClient: AIApiClient | null = null;
  private sessionId: string | null = null;

  /**
   * Initialize the AI service with API client
   */
  initialize(apiClient: AIApiClient) {
    this.apiClient = apiClient;
  }

  /**
   * Generate a new session ID
   */
  private generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Get or create session ID
   */
  private getSessionId(): string {
    if (!this.sessionId) {
      this.sessionId = this.generateSessionId();
    }
    return this.sessionId;
  }

  /**
   * Clear current session
   */
  clearSession() {
    this.sessionId = null;
  }

  /**
   * Ensure API client is initialized
   */
  private ensureInitialized() {
    if (!this.apiClient) {
      throw new Error('AIChatService not initialized. Call initialize() with an API client first.');
    }
  }

  /**
   * Send a message to the AI agent orchestration endpoint
   * Maps to POST /api/ai/agent from ai-data-aware-implementation-plan-85f4b6.md Phase 2
   */
  async sendMessage(message: string, context?: any): Promise<AIResponse> {
    this.ensureInitialized();
    
    const agentResponse = await this.apiClient!.post<any>('/api/ai/agent', {
      message,
      conversationId: context?.conversationId,
      context: {
        ...context,
        surface: surfaceForReportType(context?.reportType),
      },
    });
    
    return {
      message: agentResponse.response,
      actions: agentResponse.actions || [],
      conversationId: agentResponse.conversationId,
      model: 'gpt-4o-mini',
      tokensUsed: agentResponse.tokensUsed,
    };
  }

  /**
   * Generate a report or dashboard from natural language.
   * Uses POST /api/ai/agent; the backend derives the route from context.surface.
   */
  async generateReport(request: AIGenerateReportRequest): Promise<AIResponse> {
    this.ensureInitialized();
    
    const agentResponse = await this.apiClient!.post<any>('/api/ai/agent', {
      message: request.description,
      context: {
        reportType: request.reportType,
        surface: request.reportType === 'dashboard' ? 'dashboard' : 'report',
      },
    });
    
    return {
      message: agentResponse.response,
      actions: agentResponse.actions || [],
      conversationId: agentResponse.conversationId,
      model: 'gpt-4o-mini',
      tokensUsed: agentResponse.tokensUsed,
    };
  }

  /**
   * Refine an existing report/dashboard configuration
   * Uses POST /api/ai/agent with conversation context
   */
  async refineConfig(request: AIRefineConfigRequest): Promise<AIResponse> {
    this.ensureInitialized();
    
    const agentResponse = await this.apiClient!.post<any>('/api/ai/agent', {
      message: request.refinementPrompt,
      conversationId: request.conversationId,
      context: {
        currentConfig: request.currentConfig,
        reportType: request.reportType,
        surface: request.reportType === 'dashboard' ? 'dashboard' : 'report',
      },
    });
    
    return {
      message: agentResponse.response,
      actions: agentResponse.actions || [],
      conversationId: agentResponse.conversationId,
      model: 'gpt-4o-mini',
      tokensUsed: agentResponse.tokensUsed,
    };
  }

  /**
   * Upload and analyze a PDF document to generate a document template
   * Maps to POST /api/ai/analyze-document-pdf from ai-chatbot-template-creation-6d168a.md §4
   */
  async uploadPDF(file: File): Promise<AIResponse> {
    this.ensureInitialized();
    
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.apiClient!.post<any>('/api/ai/analyze-document-pdf', formData);
    console.log('[aiChatService.uploadPDF] API response logoPosition:', response.documentConfig?.logoPosition);
    console.log('[aiChatService.uploadPDF] Full documentConfig keys:', response.documentConfig ? Object.keys(response.documentConfig) : 'none');
    
    return {
      message: response.analysis 
        ? `PDF analyzed successfully! Found ${response.analysis.detectedSections} sections and ${response.analysis.mappedFields} fields.`
        : 'PDF uploaded successfully.',
      actions: response.documentConfig ? [{
        type: 'preview-config',
        config: {
          name: response.suggestedName || file.name.replace('.pdf', ''),
          type: 'document',
          documentConfig: response.documentConfig,
        },
        suggestedName: response.suggestedName || file.name.replace('.pdf', ''),
        explanation: `Generated from PDF: ${file.name}`,
      }] : [],
      conversationId: `pdf-${Date.now()}`,
      model: 'gpt-4o-vision',
      tokensUsed: response.tokensUsed,
    };
  }

  /**
   * Analyze a PDF document and generate a document template
   * Note: This capability is from ai-chatbot-template-creation-6d168a.md
   * and may need backend implementation as it's not in the Phase 2 spec
   */
  async analyzePDF(request: AIAnalyzePDFRequest): Promise<AIResponse> {
    this.ensureInitialized();
    
    const agentResponse = await this.apiClient!.post<any>('/api/ai/agent', {
      message: `Analyze this PDF and create a document template: ${request.fileName}`,
      context: {
        pdfUrl: request.pdfUrl,
        fileName: request.fileName,
        documentType: request.documentType,
        action: 'analyze-pdf',
        surface: 'general',
      },
    });
    
    return {
      message: agentResponse.response,
      actions: agentResponse.actions || [],
      conversationId: agentResponse.conversationId,
      model: 'gpt-4o-vision',
      tokensUsed: agentResponse.tokensUsed,
    };
  }

  /**
   * Get schema information from L1 Schema Registry
   * GET /api/ai/schema - Phase 1 endpoint from ai-data-aware-implementation-plan-85f4b6.md
   */
  async getSchemaInfo(collection?: string): Promise<AISchemaInfo[]> {
    this.ensureInitialized();
    
    const params = new URLSearchParams();
    if (collection) {
      params.append('collection', collection);
    }
    params.append('includeQueryPatterns', 'true');
    params.append('includeAggregationPatterns', 'true');

    const data = await this.apiClient!.get<any>(`/api/ai/schema?${params}`);
    return data.entries || [];
  }

  /**
   * Get data summaries from L3 Data Summaries
   * GET /api/ai/summary - Phase 1 endpoint from ai-data-aware-implementation-plan-85f4b6.md
   */
  async getDataSummary(
    summaryType: string,
    timeScope?: string,
    timePeriod?: string,
    fiscalYear?: number
  ): Promise<AIDataSummary | null> {
    this.ensureInitialized();
    
    const params = new URLSearchParams({ summaryType });
    if (timeScope) params.append('timeScope', timeScope);
    if (timePeriod) params.append('timePeriod', timePeriod);
    if (fiscalYear) params.append('fiscalYear', fiscalYear.toString());

    const data = await this.apiClient!.get<any>(`/api/ai/summary?${params}`);
    return data.summary || null;
  }

  /**
   * Search using semantic vector search (L2 Embeddings)
   * POST /api/ai/search - Phase 2 endpoint from ai-data-aware-implementation-plan-85f4b6.md
   */
  async searchOrgCatalog(query: string, reportType?: 'report' | 'dashboard' | 'document'): Promise<any[]> {
    this.ensureInitialized();
    
    const collections = reportType ? ['reportDefinitions'] : undefined;
    const filters = reportType ? { 'metadata.type': reportType } : undefined;

    const data = await this.apiClient!.post<any>('/api/ai/search', {
      query,
      collections,
      filters,
      topK: 10,
      minScore: 0.7,
    });
    
    return data.results || [];
  }

  /**
   * Adapt an existing report for the current department
   * Uses POST /api/ai/agent with adaptation context
   */
  async adaptReport(sourceReportId: string, adaptationGoal: string): Promise<any> {
    this.ensureInitialized();
    
    return await this.apiClient!.post<any>('/api/ai/agent', {
      message: `Adapt this report: ${adaptationGoal}`,
      context: {
        sourceReportId,
        adaptationGoal,
        action: 'adapt-report',
        surface: 'report',
      },
    });
  }
}

export const aiChatService = new AIChatService();
